import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { api, fmtINR, formatApiError, STATUS_COLORS, ROLES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle, XCircle, FileArrowUp, Paperclip, ArrowLeft, ClockCounterClockwise, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import WhatsAppNudge from "@/components/WhatsAppNudge";

const APPROVAL_LABELS = {
  centre_manager: "Level 1 · Centre Manager",
  centre_coordinator: "Level 2 · Centre Coordinator",
  project_head: "Level 3 · Project Head",
  director: "Level 4 · Director",
  finance: "Level 5 · Finance Team",
};

export default function AssetDetail() {
  const { id } = useParams();
  const [replacingFileId, setReplacingFileId] = useState(null);
  const nav = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const fileInput = useRef(null);
  // Origin-aware nav: return to MyQueue (or wherever the user came from)
  // when they hit Back or after they approve. Fallback = Assets list.
  const backTo = location.state?.from || "/assets";
  const [asset, setAsset] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () =>
    api.get(`/assets/${id}`)
      .then((r) => { setAsset(r.data); setLoadError(null); })
      .catch((e) => setLoadError(formatApiError(e.response?.data?.detail) || "Failed to load asset"));
  useEffect(() => { load(); }, [id]);

  if (loadError) {
    return (
      <div className="p-8 max-w-xl mx-auto" data-testid="asset-detail-error">
        <div className="bg-white border border-rose-200 p-6 rounded-sm">
          <div className="text-xs uppercase tracking-widest text-rose-700 mb-2">Could not open asset</div>
          <div className="text-sm text-zinc-800">{loadError}</div>
          <div className="mt-4 flex gap-3">
            <Button onClick={load} variant="outline" className="rounded-sm" data-testid="asset-retry-btn">Retry</Button>
            <Button onClick={() => nav(backTo)} className="rounded-sm bg-zinc-900 text-white" data-testid="asset-back-btn">Back</Button>
          </div>
        </div>
      </div>
    );
  }
  if (!asset) return (
    <div className="p-8 text-zinc-500 flex items-center gap-2" data-testid="asset-detail-loading">
      <span className="inline-block w-3 h-3 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin" />
      Loading asset…
    </div>
  );

  const canAct =
    (asset.status === "submitted" || asset.status === "in_review") &&
    (user?.role === "admin" || user?.role === asset.next_approver_role);

  const canResubmit = asset.status === "sent_back" &&
    (user?.role === "admin" || user?.role === "centre_manager" || user?.id === asset.created_by);

  const resubmit = async () => {
    const newDesc = (window.prompt("Update description (mandatory):", asset.description || "") || "").trim();
    if (!newDesc) return toast.error("Description is mandatory.");
    const payload = {
      centre_id: asset.centre_id,
      asset_name: asset.asset_name,
      asset_type: asset.asset_type || null,
      category: asset.category || null,
      vendor_id: asset.vendor_id || null,
      vendor: asset.vendor || null,
      invoice_number: asset.invoice_number || null,
      invoice_date: asset.invoice_date || null,
      purchase_date: asset.purchase_date || null,
      description: newDesc,
      quantity: asset.quantity || 1,
      unit_cost: asset.unit_cost || 0,
      total_cost: asset.total_cost || 0,
      gst_pct: asset.gst_pct ?? null,
      gst_amount: asset.gst_amount || 0,
      depreciation_pct: asset.depreciation_pct ?? null,
      useful_life_years: asset.useful_life_years ?? null,
      warranty_until: asset.warranty_until || null,
      serial_number: asset.serial_number || null,
      location: asset.location || null,
      attachments: asset.attachments || [],
    };
    setBusy(true);
    try {
      await api.post(`/assets/${id}/resubmit`, payload);
      toast.success("Resubmitted — approval workflow restarted from Level 1");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const doAction = async (verb) => {
    setBusy(true);
    try {
      await api.post(`/assets/${id}/${verb}`, { remarks });
      toast.success(verb === "approve" ? "Approved — routed to next tier" : "Rejected");
      setRemarks("");
      if (verb === "approve" && location.state?.from) {
        // Return to origin (e.g. My Queue) so the item drops out of the
        // pending list and the queue refreshes automatically.
        nav(location.state.from, { replace: true });
        return;
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const up = await api.post("/files/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.post(`/assets/${id}/attach`, {
        file_id: up.data.id,
        filename: file.name,
        kind: "invoice",
        replace_file_id: replacingFileId,
      });
      toast.success(replacingFileId ? "Attachment replaced successfully" : "Invoice attached");
      setReplacingFileId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const removeAttachment = async (fileId) => {
    if (!window.confirm("Remove this attachment?")) return;
    try {
      await api.delete(`/assets/${id}/attachments/${fileId}`);
      toast.success("Attachment removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to remove attachment");
    }
  };

  const triggerUpload = (replaceId = null) => {
    setReplacingFileId(replaceId);
    fileInput.current?.click();
  };

  const chain = asset.approval_chain || [];
  const done = asset.approvals_done || [];
  const doneByRole = Object.fromEntries(done.map((d) => [d.role, d]));
  const isPending = ["submitted", "in_review"].includes(asset.status);

  return (
    <div className="p-4 lg:p-8 space-y-6 lg:space-y-8 max-w-5xl mx-auto" data-testid="asset-detail-root">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div className="min-w-0">
          <button
            onClick={() => nav(backTo)}
            className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-zinc-500 hover:text-black mb-2"
            data-testid="asset-back"
          >
            <ArrowLeft size={12} /> Back
          </button>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
            Asset · {asset.asset_no}
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl mt-1 tracking-tight break-words">{asset.asset_name}</h1>
          <div className="mt-2 flex flex-wrap gap-2 items-center">
            <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest border ${STATUS_COLORS[asset.status] || "bg-zinc-100 text-zinc-700 border-zinc-300"}`} data-testid="asset-status">
              {asset.status}
            </span>
            <span className="text-xs text-zinc-500">
              {asset.centre_code} · {asset.centre_name}
            </span>
            <span className="text-xs text-zinc-400">
              Submitted by {asset.created_by_name} ({ROLES[asset.created_by_role] || asset.created_by_role})
            </span>
          </div>
        </div>
        <div className="sm:text-right shrink-0">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total Cost</div>
          <div className="text-2xl sm:text-3xl font-heading tabular-nums mt-1">{fmtINR(asset.total_cost)}</div>
          {asset.gst_amount ? (
            <div className="text-xs text-zinc-500">incl. GST {fmtINR(asset.gst_amount)}</div>
          ) : null}
          {isPending && (
            <div className="mt-3 sm:flex sm:justify-end">
              <WhatsAppNudge kind="asset" id={asset.id} number={asset.asset_no} />
            </div>
          )}
        </div>
      </div>

      {/* Details grid */}
      <div className="bg-white border border-zinc-200 grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-6 p-6 text-sm">
        <Field label="Type">{asset.asset_type || "—"}</Field>
        <Field label="Category">{asset.category || "—"}</Field>
        <Field label="Vendor">{asset.vendor || "—"}</Field>
        <Field label="Serial #">{asset.serial_number || "—"}</Field>
        <Field label="Invoice #">{asset.invoice_number || "—"}</Field>
        <Field label="Invoice Date">{asset.invoice_date || "—"}</Field>
        <Field label="Purchase Date">{asset.purchase_date || "—"}</Field>
        <Field label="Useful Life">{asset.useful_life_years ? `${asset.useful_life_years} yrs` : "—"}</Field>
        <Field label="Quantity">{asset.quantity}</Field>
        <Field label="Unit Cost">{fmtINR(asset.unit_cost)}</Field>
        <Field label="Location">{asset.location || "—"}</Field>
        <Field label="Warranty Until">{asset.warranty_until || "—"}</Field>
        {asset.description && (
          <div className="col-span-2 md:col-span-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Description</div>
            <div className="text-sm text-zinc-800">{asset.description}</div>
          </div>
        )}
      </div>

      {/* Approval chain */}
      <div className="bg-white border border-zinc-200 p-6">
        <div className="flex justify-between items-baseline mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Workflow</div>
            <div className="font-heading text-lg mt-1">5-Level Sequential Approval</div>
          </div>
          {asset.next_approver_role && (
            <div className="text-xs text-zinc-500" data-testid="asset-next-approver">
              Next: <b className="text-zinc-800">{APPROVAL_LABELS[asset.next_approver_role]}</b>
            </div>
          )}
        </div>
        <ol className="space-y-2">
          {chain.map((role, idx) => {
            const step = doneByRole[role];
            const isNext = role === asset.next_approver_role;
            return (
              <li
                key={role}
                data-testid={`asset-step-${role}`}
                className={`flex items-center gap-4 p-3 border ${
                  step ? "bg-emerald-50 border-emerald-200"
                       : isNext ? "bg-amber-50 border-amber-200"
                                : "bg-zinc-50 border-zinc-200"
                }`}
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium tabular-nums bg-white border">
                  {idx + 1}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium">{APPROVAL_LABELS[role] || role}</div>
                  {step ? (
                    <div className="text-xs text-emerald-900">
                      ✓ {step.name} · {new Date(step.at).toLocaleString()}
                      {step.remarks ? ` · "${step.remarks}"` : ""}
                    </div>
                  ) : isNext ? (
                    <div className="text-xs text-amber-900">Pending action</div>
                  ) : (
                    <div className="text-xs text-zinc-500">Locked — waits for previous level</div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      {/* Sent-back banner + resubmit */}
      {asset.status === "sent_back" && (
        <div className="bg-amber-50 border border-amber-300 p-6" data-testid="asset-sent-back-panel">
          <div className="flex justify-between items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-[280px]">
              <div className="text-[10px] uppercase tracking-widest text-amber-700">Sent back for correction</div>
              <div className="font-heading text-lg mt-1 text-amber-900">Please review and resubmit</div>
              {asset.sent_back_reason && (
                <div className="mt-2 text-sm text-zinc-800"><strong>Reason:</strong> {asset.sent_back_reason}</div>
              )}
              <div className="mt-1 text-xs text-zinc-600">
                {(asset.history || []).filter(h => h.action === "sent_back").slice(-1).map((h) => (
                  <div key={h.at || h.by}>Sent back by <b>{h.by}</b> · {new Date(h.at).toLocaleString()}</div>
                ))}
              </div>
            </div>
            {canResubmit && (
              <Button
                onClick={resubmit}
                disabled={busy}
                data-testid="asset-resubmit-btn"
                className="rounded-sm bg-amber-700 hover:bg-amber-800 text-white h-10 gap-2"
              >
                <ClockCounterClockwise size={14} weight="bold" />
                {busy ? "…" : "Edit & Resubmit"}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Approver actions */}
      {canAct && (
        <div className="bg-white border border-zinc-900 p-6" data-testid="asset-action-panel">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Your action required</div>
          <div className="font-heading text-lg mt-1 mb-4">
            {APPROVAL_LABELS[asset.next_approver_role] || asset.next_approver_role}
          </div>
          <Textarea
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Remarks (optional — recorded in audit trail)"
            rows={3}
            className="rounded-sm border-zinc-300"
            data-testid="asset-remarks"
          />
          <div className="mt-4 flex gap-3">
            <Button
              onClick={() => doAction("approve")}
              disabled={busy}
              data-testid="asset-approve"
              className="rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white h-10 gap-2"
            >
              <CheckCircle size={16} weight="bold" /> {busy ? "…" : "Approve"}
            </Button>
            <Button
              onClick={() => doAction("reject")}
              disabled={busy}
              variant="outline"
              data-testid="asset-reject"
              className="rounded-sm border-rose-500 text-rose-700 hover:bg-rose-50 h-10 gap-2"
            >
              <XCircle size={16} weight="bold" /> Reject
            </Button>
          </div>
        </div>
      )}

      {/* Attachments */}
      <div className="bg-white border border-zinc-200 p-6" data-testid="asset-attachments">
        <div className="flex justify-between items-baseline mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Documents</div>
            <div className="font-heading text-lg mt-1">Invoice &amp; Attachments</div>
          </div>
          <div>
            <input
              ref={fileInput}
              type="file"
              onChange={onFileSelected}
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png"
              data-testid="asset-upload-input"
            />
            <Button
              onClick={() => fileInput.current?.click()}
              disabled={uploading}
              data-testid="asset-upload-btn"
              className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white h-9 gap-2 text-xs"
            >
              <FileArrowUp size={14} weight="bold" />
              {uploading ? "Uploading…" : "Upload Invoice"}
            </Button>
          </div>
        </div>
        {(asset.attachments || []).length === 0 ? (
          <p className="text-xs text-zinc-500">No documents uploaded yet.</p>
        ) : (
          <ul className="space-y-2">
            {(asset.attachments || []).map((a, i) => {
              const fileId = a.file_id || a.id;
              const fileName = a.filename || a.original_filename || "document";
              return (
                <li key={fileId || i} className="flex items-center justify-between p-3 border border-zinc-200 rounded-sm bg-zinc-50/50 hover:bg-zinc-50">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <Paperclip size={16} className="text-zinc-500 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{fileName}</div>
                      <div className="text-xs text-zinc-500">
                        {a.kind || "document"} {a.uploaded_by ? `· uploaded by ${a.uploaded_by}` : ""} {a.uploaded_at ? `· ${new Date(a.uploaded_at).toLocaleString()}` : ""}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-4">
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${fileId}/download`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs uppercase tracking-widest text-zinc-800 hover:text-black border border-zinc-300 hover:border-zinc-800 px-2.5 py-1 bg-white"
                      data-testid={`asset-attachment-link-${i}`}
                    >
                      View →
                    </a>
                    <button
                      type="button"
                      onClick={() => triggerUpload(fileId)}
                      disabled={uploading}
                      className="text-xs uppercase tracking-widest text-emerald-700 hover:text-emerald-900 border border-emerald-300 hover:border-emerald-700 px-2.5 py-1 bg-white"
                      title="Upload a new copy of this file"
                      data-testid={`asset-attachment-replace-${i}`}
                    >
                      Re-upload
                    </button>
                    <button
                      type="button"
                      onClick={() => removeAttachment(fileId)}
                      className="p-1 text-zinc-400 hover:text-rose-600"
                      title="Remove attachment"
                      data-testid={`asset-attachment-delete-${i}`}
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* History */}
      <div className="bg-white border border-zinc-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <ClockCounterClockwise size={16} className="text-zinc-500" />
          <div className="font-heading text-lg">Activity Timeline</div>
        </div>
        <ol className="space-y-3">
          {(asset.history || []).map((h, i) => (
            <li key={`${h.at || i}-${h.action}`} className="border-l-2 border-zinc-300 pl-4 py-1">
              <div className="text-sm">
                <b>{h.by}</b> · <span className="text-zinc-600">{h.action}</span>
              </div>
              <div className="text-xs text-zinc-500">
                {new Date(h.at).toLocaleString()}
                {h.remarks ? ` — “${h.remarks}”` : ""}
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* Payment section — finance action or paid summary */}
      <AssetPayment asset={asset} user={user} onReload={load} />

      {/* Depreciation schedule */}
      {asset.useful_life_years && asset.total_cost > 0 && (
        <DepreciationSchedule
          totalCost={asset.total_cost}
          usefulLifeYears={asset.useful_life_years}
          purchaseDate={asset.purchase_date}
        />
      )}
    </div>
  );
}

// ---------- Payment sub-component ----------
function AssetPayment({ asset, user, onReload }) {
  const [pay, setPay] = useState({
    payment_mode: "bank_transfer", payment_date: new Date().toISOString().slice(0, 10),
    utr: "", bill_number: "", bank_account: "", remarks: "",
  });
  const [ackFileId, setAckFileId] = useState(null);
  const [ackName, setAckName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const ackInput = useRef(null);

  const canPay = user?.role === "finance" && asset.status === "approved";
  const alreadyPaid = asset.status === "paid";

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/files/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setAckFileId(r.data.id);
      setAckName(file.name);
      toast.success("Acknowledgment uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (ackInput.current) ackInput.current.value = "";
    }
  };

  const submit = async () => {
    if (!pay.utr && !pay.bill_number) return toast.error("Enter UTR or Bill Number");
    setBusy(true);
    try {
      await api.post(`/assets/${asset.id}/pay`, { ...pay, payment_ack_file_id: ackFileId });
      toast.success("Asset marked paid — centre manager will see the acknowledgment");
      onReload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!canPay && !alreadyPaid) return null;

  return (
    <div className={`bg-white border p-6 ${canPay ? "border-zinc-900" : "border-emerald-300 bg-emerald-50/40"}`}
      data-testid="asset-payment-panel">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">
          {alreadyPaid ? "Payment Completed" : "Finance Payment"}
        </div>
        {(alreadyPaid || asset.status === "approved") && (
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/receipts/asset/${asset.id}.pdf`}
            target="_blank" rel="noopener noreferrer"
            data-testid="download-asset-receipt-btn"
            className="text-[11px] uppercase tracking-widest text-zinc-800 hover:text-black border border-zinc-300 hover:border-zinc-800 px-3 py-1"
          >
            Download Receipt →
          </a>
        )}
      </div>
      <div className="font-heading text-lg mt-1 mb-4">
        {alreadyPaid ? `Paid by ${asset.paid_by_name}` : "Record Payment"}
      </div>

      {alreadyPaid ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <Field label="UTR">{asset.payment?.utr || "—"}</Field>
          <Field label="Bill #">{asset.payment?.bill_number || "—"}</Field>
          <Field label="Payment Mode">{asset.payment?.payment_mode || "—"}</Field>
          <Field label="Payment Date">{asset.payment?.payment_date || "—"}</Field>
          {asset.payment?.remarks && (
            <div className="col-span-full">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Remarks</div>
              <div className="text-sm">{asset.payment.remarks}</div>
            </div>
          )}
          {asset.payment?.payment_ack_file_id && (
            <div className="col-span-full">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Payment Acknowledgment</div>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${asset.payment.payment_ack_file_id}/download`}
                target="_blank" rel="noopener noreferrer"
                data-testid="asset-payment-ack-link"
                className="inline-flex items-center gap-2 text-sm text-zinc-800 hover:text-black underline">
                <Paperclip size={14} /> Download acknowledgment
              </a>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-widest">UTR / Ref #</Label>
            <Input value={pay.utr} onChange={(e) => setPay({ ...pay, utr: e.target.value })}
              className="rounded-sm h-10 mt-2" data-testid="asset-pay-utr" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Bill #</Label>
            <Input value={pay.bill_number} onChange={(e) => setPay({ ...pay, bill_number: e.target.value })}
              className="rounded-sm h-10 mt-2" data-testid="asset-pay-bill" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Payment Date</Label>
            <Input type="date" value={pay.payment_date}
              onChange={(e) => setPay({ ...pay, payment_date: e.target.value })}
              className="rounded-sm h-10 mt-2" />
          </div>
          <div className="col-span-full">
            <Label className="text-xs uppercase tracking-widest">Payment Acknowledgment (PDF/image)</Label>
            <input ref={ackInput} type="file" onChange={upload} className="hidden"
              accept=".pdf,.jpg,.jpeg,.png" data-testid="asset-pay-ack-input" />
            <div className="mt-2 flex items-center gap-3">
              <Button type="button" onClick={() => ackInput.current?.click()} disabled={uploading}
                data-testid="asset-pay-ack-btn"
                variant="outline" className="rounded-sm h-10 gap-2 text-xs">
                <FileArrowUp size={14} /> {uploading ? "Uploading…" : (ackName ? "Replace" : "Upload")}
              </Button>
              {ackName && <span className="text-xs text-zinc-600 truncate">{ackName}</span>}
            </div>
          </div>
          <div className="col-span-full">
            <Label className="text-xs uppercase tracking-widest">Remarks</Label>
            <Input value={pay.remarks} onChange={(e) => setPay({ ...pay, remarks: e.target.value })}
              className="rounded-sm h-10 mt-2" />
          </div>
          <div className="col-span-full">
            <Button onClick={submit} disabled={busy} data-testid="asset-pay-submit"
              className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white h-10">
              {busy ? "Recording…" : "Mark Paid"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Depreciation schedule sub-component ----------
function DepreciationSchedule({ totalCost, usefulLifeYears, purchaseDate }) {
  const yearlyDep = totalCost / usefulLifeYears;
  const monthlyDep = yearlyDep / 12;
  const startYear = purchaseDate ? new Date(purchaseDate).getFullYear() : new Date().getFullYear();
  const rows = Array.from({ length: usefulLifeYears }, (_, i) => {
    const yr = startYear + i;
    const opening = totalCost - yearlyDep * i;
    const closing = Math.max(0, opening - yearlyDep);
    return { yr, opening, dep: yearlyDep, closing };
  });

  return (
    <div className="bg-white border border-zinc-200 p-6" data-testid="asset-depreciation">
      <div className="flex justify-between items-baseline mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Schedule</div>
          <div className="font-heading text-lg mt-1">Straight-Line Depreciation</div>
        </div>
        <div className="text-right text-xs text-zinc-500">
          <div>Yearly · <b className="text-zinc-800 tabular-nums">{fmtINR(yearlyDep)}</b></div>
          <div>Monthly · <b className="text-zinc-800 tabular-nums">{fmtINR(monthlyDep)}</b></div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left p-3">Year</th>
              <th className="text-right p-3">Opening Book Value</th>
              <th className="text-right p-3">Depreciation</th>
              <th className="text-right p-3">Closing Book Value</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {rows.map((r) => (
              <tr key={r.yr} className="border-t border-zinc-100">
                <td className="p-3 font-mono text-xs">{r.yr}</td>
                <td className="p-3 text-right">{fmtINR(r.opening)}</td>
                <td className="p-3 text-right text-rose-700">-{fmtINR(r.dep)}</td>
                <td className="p-3 text-right font-medium">{fmtINR(r.closing)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
    <div className="text-sm text-zinc-800">{children}</div>
  </div>
);
